import React, { useCallback, useState, useEffect } from "react"
import { useDropzone } from "react-dropzone"
import { Button, Flex, Typography } from "@kohler-dsm-library/kohler-core"
import {
  ACCEPTED_FORMATS,
  IMAGE_FILE_TYPE,
} from "@/components/VisualSearch/v1/UploadImage/constant"
import { useTranslation } from "next-i18next"
import { getTranslations } from "@/components/GlobalVisualSearch/localization"
import Style from "@/components/GlobalVisualSearch/FileUpload/index.module.scss"
import useViewport from "@/hooks/useViewport"

const MAX_FILE_SIZE_MB = 20

const FileUpload = props => {
  const { setImage } = props || {}
  const [error, setError] = useState("")
  const { t } = useTranslation("common")
  const staticText = getTranslations(t)
  const { isMediumScreen } = useViewport()
  let heic2any = null
  useEffect(() => {
    import("heic2any").then(module => {
      heic2any = module.default
    })
  }, [])
  const {
    dragAndDrop = "",
    dropFilesHere = "",
    orText = "",
    uploadFileButton = "",
    imageUploadFileExtError = "",
    imageUploadFileSizeError = "",
  } = staticText
  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      setError("")
      if (fileRejections.length > 0) {
        const rejection = fileRejections[0]
        const { errors } = rejection

        errors.forEach(err => {
          if (err.code === "file-too-large") {
            setError(imageUploadFileSizeError(MAX_FILE_SIZE_MB))
          } else if (err.code === "file-invalid-type") {
            setError(imageUploadFileExtError)
          }
        })
        return
      }
      let file = acceptedFiles[0]
      const heicType = ["image/heif", "image/heic"]
      const isHeicimg = heicType.includes(file?.type)
      if (isHeicimg) {
        heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9,
        }).then(res => {
          file = new File([res], file.name, {
            type: res.type,
            lastModified: new Date().getTime(),
          })
          if (file) {
            reader.readAsDataURL(file)
          }
        })
      }
      const reader = new FileReader()
      reader.onload = event => {
        const dataURL = event.target.result
        const img = new Image()
        img.onload = () => {
          const fileData = {
            file,
            width: img.width,
            height: img.height,
            dataURL,
          }
          setImage([fileData])
        }
        img.src = dataURL
      }
      if (!isHeicimg && file) {
        reader.readAsDataURL(file)
      }
    },
    [setImage]
  )

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    maxSize: MAX_FILE_SIZE_MB * 1024 * 1024, // 20MB max
    accept: ACCEPTED_FORMATS,
    noClick: true,
    noKeyboard: true,
  })

  return (
    <Flex
      direction="column"
      justify="center"
      align="center"
      gap="25px"
      className={`${Style.FileUploadContainer} ${error && "pb-27"}`}
      {...getRootProps()}
    >
      <Flex
        direction="column"
        justify="center"
        align="center"
        gap="25px"
        className={Style.DropzoneArea}
      >
        <input
          accept={IMAGE_FILE_TYPE}
          aria-label="Input Images"
          {...getInputProps()}
        />
        <Flex direction="column" justify="center" align="center" gap="15px">
          <Typography component={"h5"} size={"heading-h5"}>
            {isDragActive ? dropFilesHere : dragAndDrop}
          </Typography>
          <Typography size={"label-regular-m"}>{orText}</Typography>
        </Flex>
        <Flex direction="column" justify="center" align="center" gap="10px">
          <Button
            size="small"
            fullWidth={isMediumScreen}
            variant="secondary"
            onClick={open}
          >
            {uploadFileButton}
          </Button>
          {error && (
            <Typography
              size={"label-regular-xs"}
              className={Style.ImageUploadError}
            >
              {error}
            </Typography>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default FileUpload
